import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Progress } from '@/components/ui/progress';
import { Loader2, CheckCircle2, XCircle, Archive } from 'lucide-react';

export type DownloadState = 'idle' | 'downloading' | 'done' | 'error';

interface DownloadProgressModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  state: DownloadState;
  progress: number;
  fileName: string;
  fileSize: string;
  error?: string;
}

export function DownloadProgressModal({
  open,
  onOpenChange,
  state,
  progress,
  fileName,
  fileSize,
  error,
}: DownloadProgressModalProps) {
  const getStatusIcon = () => {
    switch (state) {
      case 'error':
        return <XCircle className="h-12 w-12 text-red-500" />;
      case 'done':
        return <CheckCircle2 className="h-12 w-12 text-green-500" />;
      case 'downloading':
        return <Loader2 className="h-12 w-12 text-primary animate-spin" />;
      default:
        return <Archive className="h-12 w-12 text-gray-400" />;
    }
  };

  const getStatusText = () => {
    switch (state) {
      case 'downloading':
        return 'Telechargement en cours...';
      case 'done':
        return 'Telechargement termine';
      case 'error':
        return 'Erreur';
      default:
        return '';
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="sm:max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>Telechargement de la sauvegarde</AlertDialogTitle>
          <AlertDialogDescription>{fileName}</AlertDialogDescription>
        </AlertDialogHeader>

        <div className="flex flex-col items-center gap-4 py-6">
          {getStatusIcon()}
          <p className="text-sm font-medium text-gray-700">{getStatusText()}</p>

          {state === 'downloading' && (
            <div className="w-full space-y-2">
              <div className="flex items-center gap-3">
                <Progress value={progress} className="h-3 flex-1" />
                <span className="text-xs text-gray-500 tabular-nums">
                  {progress > 0 ? `${progress}%` : ''}
                </span>
              </div>
              {fileSize && (
                <p className="text-xs text-gray-500 text-center">{fileSize}</p>
              )}
            </div>
          )}

          {state === 'done' && fileSize && (
            <p className="text-xs text-gray-500">Taille : {fileSize}</p>
          )}

          {state === 'error' && (
            <p className="text-sm text-red-600 text-center">
              {error || 'Une erreur est survenue lors du telechargement'}
            </p>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={state === 'downloading'}>
            Fermer
          </AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const SIZE_UNITS = ['o', 'Ko', 'Mo', 'Go', 'To'];

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 o';
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(1)} ${SIZE_UNITS[i]}`;
}
